// Shared pure helpers for the price grid + the kiosk/location price tabs.
// Single home for the price-domain primitives (weight, scope key, GenericPrice
// builder, formatting). No React, no I/O.
import type { GenericPrice } from "../api/price.ts";

/* ── Weight bitmask (mirrors S_Generic_Price.Weight / Web_Generic_Price_View) ── */
export const WEIGHT = {
  State: 1, District: 2, Chain: 4, Location: 8, Kiosk: 16,
  Category: 32, Type: 64, Product: 128, OperatorID: 32768,
} as const;

export const WEIGHT_KIOSK_PRODUCT = WEIGHT.Kiosk | WEIGHT.Product;        // 144
export const WEIGHT_LOCATION_PRODUCT = WEIGHT.Location | WEIGHT.Product;  // 136

/** Decode a specificity weight into the scope dimensions it's built from, so a
 *  resolved price's origin reads as "Product + Location" instead of "136". */
export function describeWeight(weight: number): string {
  if (!weight) return "";
  const order: [number, string][] = [
    [WEIGHT.Product, "Product"],
    [WEIGHT.OperatorID, "Operator"],
    [WEIGHT.State, "State"],
    [WEIGHT.District, "District"],
    [WEIGHT.Chain, "Chain"],
    [WEIGHT.Location, "Location"],
    [WEIGHT.Kiosk, "Kiosk"],
    [WEIGHT.Category, "Category"],
    [WEIGHT.Type, "Type"],
  ];
  const names = order.filter(([bit]) => (weight & bit) === bit).map(([, name]) => name);
  return names.length > 0 ? names.join(" + ") : `#${weight}`;
}

/** Additive specificity weight from a scope's set (non-zero / non-null) fields. */
export function calcWeight(s: Partial<GenericPrice>): number {
  let w = 0;
  if (s.State) w |= WEIGHT.State;
  if (s.District) w |= WEIGHT.District;
  if (s.Chain) w |= WEIGHT.Chain;
  if (s.Location) w |= WEIGHT.Location;
  if (s.Kiosk) w |= WEIGHT.Kiosk;
  if (s.Category) w |= WEIGHT.Category;
  if (s.Type) w |= WEIGHT.Type;
  if (s.Product) w |= WEIGHT.Product;
  if (s.OperatorID) w |= WEIGHT.OperatorID;
  return w;
}

export const EPS = 0.00005;

export interface CommonCacheItem {
  TypeName?: string;
  Value?: string;
  Title?: string;
  sName?: string;
  sFilterValue?: string;
  sDisplayText?: string;
}
export function ccName(c: CommonCacheItem) { return c.TypeName ?? c.sName ?? ""; }
export function ccValue(c: CommonCacheItem) { return c.Value ?? c.sFilterValue ?? ""; }
export function ccTitle(c: CommonCacheItem) { return c.Title ?? c.sDisplayText ?? ""; }

/** Always show ≥2 decimals, up to 4 if present. */
export function fmtPrice(v: number | null | undefined): string {
  if (v == null) return "—";
  const s = v.toFixed(4);
  return s.replace(/(\.[0-9]{2}[0-9]*?)0+$/, "$1");
}

/** Unique scope key (all scope fields except PriceType/Value). */
export function rowKey(s: Partial<GenericPrice>): string {
  return `P${s.Product ?? 0}|T${s.Type ?? 0}|C${s.Category ?? 0}|L${s.Location ?? 0}|K${s.Kiosk ?? 0}|S${s.State ?? 0}|Ch${s.Chain ?? 0}|D${s.District ?? 0}|O${s.OperatorID ?? 0}`;
}

/** Construct a GenericPrice for one scope + price type. Nullable scope fields
 *  coerce 0 → null (unset); an empty/negative value marks a delete (-1, Deleted).
 *  Single construction path for all price save flows (grid + kiosk/location tabs). */
export function makeGenericPrice(
  scope: Partial<GenericPrice>, priceType: number, value: number | null, weight: number,
): GenericPrice {
  return {
    Product: scope.Product ?? 0,
    Type: scope.Type || null,
    Category: scope.Category || null,
    Kiosk: scope.Kiosk || null,
    Location: scope.Location || null,
    Chain: scope.Chain || null,
    District: scope.District || null,
    State: scope.State || null,
    OperatorID: scope.OperatorID || null,
    PriceType: priceType,
    Value: value ?? -1,
    Weight: weight,
    CalcTS: 0,
    Deleted: value === null || value < 0,
  };
}

export function buildCommonNameMap(common: CommonCacheItem[], sName: string): Map<number, string> {
  const m = new Map<number, string>();
  for (const c of common) {
    if (ccName(c).toLowerCase() !== sName) continue;
    const id = parseInt(ccValue(c)) || 0;
    if (id > 0) m.set(id, ccTitle(c) || ccValue(c));
  }
  return m;
}

export function resolveCommonId(common: CommonCacheItem[], sName: string, raw: string): number {
  if (!raw) return 0;
  for (const c of common) {
    if (ccName(c).toLowerCase() !== sName) continue;
    if (ccValue(c) === raw || ccTitle(c) === raw) return parseInt(ccValue(c)) || 0;
  }
  return 0;
}

export interface ScopeMaps {
  operatorMap: Map<number, string>;
  /** State id → name. Resolves via the `sState` CB list (see
   *  S_Kiosk_Product_Price_FN: State ← S_Admin_CB_Values sName='sState'). */
  stateMap: Map<number, string>;
  /** District id → name. Resolves via the `sDistrict` CB list (distinct from State). */
  districtMap: Map<number, string>;
  chainMap: Map<number, string>;
  categoryMap: Map<number, string>;
  locationMap: Map<number, string>;
  kioskLabelMap: Map<number, string>;
}

/** One scope dimension split into its kind ("Location") and resolved value
 *  ("Instant Systems Test Location") so callers can style them separately. */
export interface ScopePart {
  kind: string;
  value: string;
}

/** Structured scope breakdown — empty array means a product default (no scope). */
export function scopeParts(s: GenericPrice, maps: ScopeMaps): ScopePart[] {
  const parts: ScopePart[] = [];
  if (s.OperatorID) parts.push({ kind: "Operator", value: String(maps.operatorMap.get(s.OperatorID) ?? `#${s.OperatorID}`) });
  if (s.State) parts.push({ kind: "State", value: String(maps.stateMap.get(s.State) ?? s.State) });
  if (s.District) parts.push({ kind: "District", value: String(maps.districtMap.get(s.District) ?? s.District) });
  if (s.Chain) parts.push({ kind: "Chain", value: String(maps.chainMap.get(s.Chain) ?? s.Chain) });
  if (s.Category) parts.push({ kind: "Category", value: String(maps.categoryMap.get(s.Category) ?? `#${s.Category}`) });
  if (s.Location) parts.push({ kind: "Location", value: String(maps.locationMap.get(s.Location) ?? `#${s.Location}`) });
  if (s.Kiosk) parts.push({ kind: "Kiosk", value: String(maps.kioskLabelMap.get(s.Kiosk) ?? `#${s.Kiosk}`) });
  return parts;
}

export function scopeLabel(s: GenericPrice, maps: ScopeMaps): string {
  const parts = scopeParts(s, maps);
  return parts.length > 0 ? parts.map((p) => `${p.kind}: ${p.value}`).join(" + ") : "Product Default";
}

/**
 * Scope parts read from a row's RAW cell values, honouring the DB semantics:
 * NULL = unset (hidden), any present value INCLUDING 0 = a specified scope dim
 * (shown). `get(name)` returns the trimmed cell string, or null when the cell is
 * NULL/empty. Mirrors S_Generic_Price.[Weight] (bit set iff dim IS NOT NULL).
 */
export function scopePartsFromRaw(get: (name: string) => string | null, maps: ScopeMaps): ScopePart[] {
  const parts: ScopePart[] = [];
  const add = (name: string, kind: string, map?: Map<number, string>) => {
    const raw = get(name);
    if (raw == null) return; // NULL/unset → hide (0 is NOT null → shown)
    const id = Number(raw);
    const label = (map && Number.isFinite(id) ? map.get(id) : undefined) ?? raw;
    parts.push({ kind, value: String(label) });
  };
  add("OperatorID", "Operator", maps.operatorMap);
  add("State", "State", maps.stateMap);
  add("District", "District", maps.districtMap);
  add("Chain", "Chain", maps.chainMap);
  add("Category", "Category", maps.categoryMap);
  add("Location", "Location", maps.locationMap);
  add("Kiosk", "Kiosk", maps.kioskLabelMap);
  return parts;
}

export interface CascadeScope {
  /** Set for the kiosk tab; undefined for the location tab (kiosk-scoped rows excluded). */
  kioskId?: number;
  locationId: number;
  operatorId: number;
  chainId: number;
  districtId: number;
  /** State id (`sState` CB list) — distinct from districtId. 0 when the caller
   *  can't resolve it (then State-scoped overrides are conservatively excluded,
   *  rather than wrongly matched against districtId). */
  stateId: number;
}

/** A setting applies to the given scope only when every dimension it sets matches.
    Category/Type-scoped rows can't be verified client-side, so they're excluded.
    Kiosk-scoped rows apply only when scope.kioskId matches — so for the location
    tab (kioskId undefined) they're excluded from the cascade. */
export function appliesToScope(s: GenericPrice, scope: CascadeScope): boolean {
  if (s.Category || s.Type) return false;
  if (s.Kiosk && (scope.kioskId == null || s.Kiosk !== scope.kioskId)) return false;
  if (s.Location && s.Location !== scope.locationId) return false;
  if (s.OperatorID && s.OperatorID !== scope.operatorId) return false;
  if (s.Chain && s.Chain !== scope.chainId) return false;
  if (s.District && s.District !== scope.districtId) return false;
  if (s.State && s.State !== scope.stateId) return false;
  return true;
}
